import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Grid,
  DialogActions,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  Autocomplete,
  Card,
  CardContent,
  Stack,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  alpha,
  Divider,
  Avatar,
  Badge,
  Chip,
  Snackbar,
  InputAdornment,
  DialogContentText,
  DialogActions as MuiDialogActions,
} from "@mui/material";
import {
  Receipt,
  Payment,
  TrendingUp,
  AccountBalance,
  Add,
  Download,
  Send,
  CheckCircle,
  Pending,
  Refresh,
  MoreVert,
  AttachMoney,
  CalendarToday,
  Person,
  Close,
  Warning,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { fetchInvoices, createInvoice, updateInvoiceStatus, downloadInvoicePDF } from "../../features/billing/billingSlice";
import { fetchPatients, getPatientDetails, clearSelectedPatient } from "../../features/patients/patientSlice";
import { styled } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import { format } from 'date-fns';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: 'blur(8px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
}));

const StatusChip = styled(Badge)(({ theme, status }) => {
  let backgroundColor, color;
  if (status === "paid") {
    backgroundColor = alpha(theme.palette.success.main, 0.1);
    color = theme.palette.success.main;
  } else if (status === "cancelled") {
    backgroundColor = alpha(theme.palette.error.main, 0.1);
    color = theme.palette.error.main;
  } else {
    backgroundColor = alpha(theme.palette.warning.main, 0.1);
    color = theme.palette.warning.main;
  }
  
  return {
    backgroundColor,
    color,
    fontWeight: 500,
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '0.75rem',
    '& .MuiBadge-badge': {
      color: 'inherit',
    },
  };
});

const Billing = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const { invoices, loading, error, count, next, previous } = useSelector((state) => state.billing);
  const patientsState = useSelector((state) => state.patients);
  const patients = Array.isArray(patientsState?.data) 
    ? patientsState.data 
    : Array.isArray(patientsState?.results) 
    ? patientsState.results 
    : Array.isArray(patientsState?.patients)
    ? patientsState.patients
    : [];
  const patientsLoading = patientsState?.loading || false;

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openModal, setOpenModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchTerm, setSearchTerm] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: '', data: null });
  const [invoiceDetails, setInvoiceDetails] = useState({
    patientId: "",
    patientName: "",
    consultationDate: "",
    amount: "",
  });

  useEffect(() => {
    dispatch(fetchInvoices({ page, page_size: rowsPerPage }));
  }, [dispatch, page, rowsPerPage]);

  useEffect(() => {
    // Fetch patients for autocomplete - fetch once on mount with higher limit
    dispatch(fetchPatients({ search: "", page: 1, limit: 100 }));
  }, [dispatch]); // Only fetch once on mount

  const handlePageChange = (_, newPage) => {
    setPage(newPage + 1);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleOpenModal = () => {
    setInvoiceDetails({ patientId: "", patientName: "", consultationDate: "", amount: "" });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setValidationErrors({});
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setInvoiceDetails((prevDetails) => ({ ...prevDetails, [name]: value }));
  };

  const handlePatientSelect = (event, value) => {
    if (value && typeof value === 'object') {
      const patientId = value?.id || value?.user?.id || null;
      const patientName = value?.name || value?.user?.name || value?.patient?.name || '';
      setInvoiceDetails({
        patientId: patientId,
        patientName: patientName,
        consultationDate: invoiceDetails.consultationDate,
        amount: invoiceDetails.amount,
      });
    } else {
      setInvoiceDetails({
        patientId: "",
        patientName: "",
        consultationDate: invoiceDetails.consultationDate,
        amount: invoiceDetails.amount,
      });
    }
  };

  const handleGenerateInvoice = async () => {
    if (!invoiceDetails.patientId && !invoiceDetails.patientName) {
      setValidationErrors({ patient: "Please select or enter a patient name." });
      return;
    }
    if (!invoiceDetails.consultationDate) {
      setValidationErrors({ consultationDate: "Consultation date is required." });
      return;
    }
    if (!invoiceDetails.amount || invoiceDetails.amount <= 0) {
      setValidationErrors({ amount: "Valid amount is required." });
      return;
    }

    try {
      const formattedInvoice = {
        patient_id: invoiceDetails.patientId || null,
        patient_name: invoiceDetails.patientName || null,
        consultation_date: invoiceDetails.consultationDate,
        amount: parseFloat(invoiceDetails.amount),
      };

      await dispatch(createInvoice(formattedInvoice)).unwrap();
      setSnackbar({
        open: true,
        message: 'Invoice created successfully!',
        severity: 'success'
      });
      handleCloseModal();
      dispatch(fetchInvoices({ page, page_size: rowsPerPage }));
    } catch (err) {
      const errorMessage = err?.detail || err?.error || err?.message || err || 'Failed to create invoice';
      setValidationErrors(typeof errorMessage === 'object' ? errorMessage : {});
      setSnackbar({
        open: true,
        message: typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage),
        severity: 'error'
      });
    }
  };

  const handleDownloadPDF = async (invoiceId) => {
    try {
              await dispatch(downloadInvoicePDF(invoiceId)).unwrap();
      setSnackbar({
        open: true,
        message: 'Invoice downloaded successfully!',
        severity: 'success'
      });
    } catch (error) {
      const errorMessage = error?.detail || error?.error || error?.message || error || 'Failed to download invoice';
      setSnackbar({
        open: true,
        message: typeof errorMessage === 'string' ? errorMessage : 'Failed to download invoice',
        severity: 'error'
      });
    }
  };

  const handleUpdateStatus = async (invoiceId, status) => {
    setConfirmDialog({
      open: true,
      type: 'status',
      data: { invoiceId, status }
    });
  };

  const handleConfirmAction = async () => {
    const { type, data } = confirmDialog;
    
    try {
      if (type === 'status') {
        await dispatch(updateInvoiceStatus(data)).unwrap();
        setSnackbar({
          open: true,
          message: `Invoice marked as ${data.status} successfully!`,
          severity: 'success'
        });
        dispatch(fetchInvoices({ page, page_size: rowsPerPage }));
      }
      setConfirmDialog({ open: false, type: '', data: null });
    } catch (error) {
      const errorMessage = error?.detail || error?.error || error?.message || error || 'Failed to update invoice status';
      setSnackbar({
        open: true,
        message: typeof errorMessage === 'string' ? errorMessage : 'Failed to update invoice status',
        severity: 'error'
      });
    }
  };

  // Calculate statistics - accurate calculations with proper handling
  const stats = React.useMemo(() => {
    if (!invoices || invoices.length === 0) {
      return {
        totalRevenue: 0,
        pendingAmount: 0,
        totalInvoices: 0,
        paidInvoices: 0,
        cancelledInvoices: 0,
        cancelledAmount: 0,
      };
    }

    let totalRevenue = 0;
    let pendingAmount = 0;
    let cancelledAmount = 0;
    let paidInvoices = 0;
    let cancelledInvoices = 0;

    invoices.forEach((invoice) => {
      const amount = parseFloat(invoice.amount || 0);
      
      if (invoice.status === 'paid') {
        totalRevenue += amount;
        paidInvoices += 1;
      } else if (invoice.status === 'pending') {
        pendingAmount += amount;
      } else if (invoice.status === 'cancelled') {
        cancelledAmount += amount;
        cancelledInvoices += 1;
      }
    });

    return {
      totalRevenue: Number(totalRevenue.toFixed(2)),
      pendingAmount: Number(pendingAmount.toFixed(2)),
      cancelledAmount: Number(cancelledAmount.toFixed(2)),
      totalInvoices: invoices.length,
      paidInvoices,
      cancelledInvoices,
    };
  }, [invoices]);

  const { totalRevenue, pendingAmount, totalInvoices, paidInvoices } = stats;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Billing & Invoices
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your billing and track payments
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => dispatch(fetchInvoices({ page, page_size: rowsPerPage }))}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenModal}
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            }}
          >
            New Invoice
          </Button>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Total Revenue
                  </Typography>
                  <AttachMoney color="success" />
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  ₹{totalRevenue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Stack>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Pending Amount
                  </Typography>
                  <Pending color="warning" />
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  ₹{pendingAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Stack>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Total Invoices
                  </Typography>
                  <Receipt color="primary" />
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {totalInvoices}
                </Typography>
              </Stack>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Paid Invoices
                  </Typography>
                  <CheckCircle color="success" />
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {paidInvoices}
                </Typography>
              </Stack>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Invoices Table */}
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="400px">
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert 
                severity="error" 
                sx={{ m: 2 }}
                action={
                  <Button 
                    color="inherit" 
                    size="small" 
                    onClick={() => dispatch(fetchInvoices({ page, page_size: rowsPerPage }))}
                  >
                    Retry
                  </Button>
                }
              >
                {typeof error === 'string' ? error : error?.detail || error?.error || 'Failed to load invoices'}
              </Alert>
            ) : invoices.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Receipt sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No invoices found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Create your first invoice to get started
                </Typography>
              </Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Patient</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow
                      key={invoice.id}
                      sx={{
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        },
                      }}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: theme.palette.primary.main,
                            }}
                          >
                            {(invoice.patient_name || 'U').charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {invoice.patient_name || 'Unknown Patient'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Invoice #{invoice.id?.toString().substring(0, 8) || 'N/A'}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarToday fontSize="small" color="action" />
                            {invoice.consultation_date 
                              ? format(new Date(invoice.consultation_date), 'MMM dd, yyyy')
                              : 'N/A'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle1" fontWeight={600}>
                          ₹{invoice.amount ? parseFloat(invoice.amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={invoice.status === 'paid' ? <CheckCircle /> : invoice.status === 'cancelled' ? <Close /> : <Pending />}
                          label={invoice.status === 'paid' ? 'Paid' : invoice.status === 'cancelled' ? 'Cancelled' : 'Pending'}
                          color={invoice.status === 'paid' ? 'success' : invoice.status === 'cancelled' ? 'error' : 'warning'}
                          variant="outlined"
                          size="small"
                          sx={{
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="Download">
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadPDF(invoice.id)}
                              sx={{
                                color: theme.palette.primary.main,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                },
                              }}
                            >
                              <Download />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Send">
                            <IconButton
                              size="small"
                              sx={{
                                color: theme.palette.info.main,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                                },
                              }}
                            >
                              <Send />
                            </IconButton>
                          </Tooltip>
                          {invoice.status === 'pending' && (
                            <Tooltip title="Mark as Paid">
                              <IconButton
                                size="small"
                                onClick={() => handleUpdateStatus(invoice.id, 'paid')}
                                sx={{
                                  color: theme.palette.success.main,
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                                  },
                                }}
                              >
                                <CheckCircle />
                              </IconButton>
                            </Tooltip>
                          )}
                          {invoice.status === 'paid' && (
                            <Tooltip title="Mark as Pending">
                              <IconButton
                                size="small"
                                onClick={() => handleUpdateStatus(invoice.id, 'pending')}
                                sx={{
                                  color: theme.palette.warning.main,
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.warning.main, 0.1),
                                  },
                                }}
                              >
                                <Pending />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </motion.div>
      </AnimatePresence>

      {/* Pagination */}
      <Box display="flex" justifyContent="center" mt={3}>
        <TablePagination
          component="div"
          count={count}
          page={page - 1}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Box>

      {/* New Invoice Dialog */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Generate New Invoice</Typography>
            <IconButton onClick={handleCloseModal}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleGenerateInvoice}>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <Autocomplete
                options={patients || []}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  const name = option?.name || option?.user?.name || option?.patient?.name || 'Unknown';
                  const email = option?.email || option?.user?.email || '';
                  const phone = option?.phone_number || option?.user?.phone_number || '';
                  const identifier = email || phone || '';
                  return identifier ? `${name} (${identifier})` : name;
                }}
                onChange={handlePatientSelect}
                loading={patientsLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Patient"
                    fullWidth
                    error={!!validationErrors.patient}
                    helperText={validationErrors.patient}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {patientsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              {!invoiceDetails.patientId && (
                <TextField
                  label="Or Enter Patient Name"
                  name="patientName"
                  value={invoiceDetails.patientName}
                  onChange={handleInputChange}
                  fullWidth
                  error={!!validationErrors.patient}
                  helperText={validationErrors.patient}
                />
              )}
              <TextField
                label="Consultation Date"
                name="consultationDate"
                type="date"
                value={invoiceDetails.consultationDate}
                onChange={handleInputChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!validationErrors.consultationDate}
                helperText={validationErrors.consultationDate}
              />
              <TextField
                label="Amount (₹)"
                name="amount"
                type="number"
                value={invoiceDetails.amount}
                onChange={handleInputChange}
                fullWidth
                error={!!validationErrors.amount}
                helperText={validationErrors.amount}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
            </Stack>
            <DialogActions sx={{ mt: 3 }}>
              <Button onClick={handleCloseModal}>Cancel</Button>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                }}
              >
                Generate Invoice
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, type: '', data: null })}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="warning" />
            Confirm Action
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to mark this invoice as {confirmDialog.data?.status}?
          </DialogContentText>
        </DialogContent>
        <MuiDialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, type: '', data: null })}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color="primary"
          >
            Confirm
          </Button>
        </MuiDialogActions>
      </Dialog>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Billing;
